import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAlert } from '../hooks/useAlert';
import { getStoredNotifications, markAllRead, type StoredNotification } from '../services/notificationStore';
import Icon from '../components/Icon';
import { IconButton } from '../components/UI';

function timeAgo(ts: number): string {
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

type FilterTab = 'all' | 'account' | 'transactions' | 'system';

const TABS: { id: FilterTab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'account', label: 'Account' },
  { id: 'transactions', label: 'Transactions' },
  { id: 'system', label: 'System' },
];

function categorize(n: StoredNotification): FilterTab {
  if (n.type === 'order' || /top.?up|withdraw|wallet/i.test(n.title + n.body)) return 'transactions';
  if (n.type === 'UPDATE' || n.type === 'system') return 'system';
  return 'account';
}

function NotificationItem({ item }: { item: StoredNotification }) {
  const { colors } = useTheme();
  const { alert } = useAlert();
  const isUpdate = item.type === 'UPDATE';

  const handleClick = () => {
    if (isUpdate && item.apkUrl) {
      alert(
        'Install Update',
        `Download v${item.title.replace(/.*v/, '')}?`,
        [
          { text: 'Later', style: 'cancel' },
          {
            text: 'Download Now',
            onPress: () => window.open(item.apkUrl!, '_blank', 'noopener'),
          },
        ],
      );
    }
  };

  return (
    <div
      onClick={handleClick}
      style={{
        display: 'flex', gap: 12,
        padding: 14, borderRadius: 14,
        backgroundColor: colors.card,
        border: `1px solid ${item.read ? colors.cardBorder : colors.accent}`,
        marginBottom: 10, minHeight: 76,
        cursor: isUpdate ? 'pointer' : 'default',
      }}
    >
      <div
        style={{
          width: 36, height: 36, borderRadius: 10,
          backgroundColor: colors.accentSoft,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon name={isUpdate ? 'download' : 'bell'} size={14} color={colors.accentText} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <div style={{
            fontSize: 13, fontWeight: 700, color: colors.text, flex: 1,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {item.title}
          </div>
          {!item.read && <div style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: colors.accent }} />}
        </div>
        <div style={{
          fontSize: 12, lineHeight: 1.5, color: colors.textSub,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
          {item.body}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
          <div style={{ fontSize: 11, color: colors.textMuted }}>{timeAgo(item.receivedAt)}</div>
          {isUpdate && <div style={{ fontSize: 11, fontWeight: 600, color: colors.accentText }}>Tap to download</div>}
        </div>
      </div>
    </div>
  );
}

export default function Notifications() {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [notifications, setNotifications] = useState<StoredNotification[]>([]);
  const [filter, setFilter] = useState<FilterTab>('all');

  const load = useCallback(async () => {
    setNotifications(await getStoredNotifications());
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleMarkAllRead = async () => {
    await markAllRead();
    await load();
  };

  const filtered = useMemo(() => {
    if (filter === 'all') return notifications;
    return notifications.filter(n => categorize(n) === filter);
  }, [notifications, filter]);

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: colors.bg }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '14px 16px',
          paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))',
          backgroundColor: colors.header,
          borderBottom: `1px solid ${colors.headerBorder}`,
          position: 'sticky', top: 0, zIndex: 10,
        }}
      >
        <IconButton icon="arrow-left" accessibilityLabel="Back" onPress={() => navigate(-1)} />
        <div style={{ fontSize: 18, fontWeight: 800, color: colors.text }}>Notifications</div>
      </div>

      <div style={{ display: 'flex', gap: 8, padding: '12px 16px 8px', flexWrap: 'wrap' }}>
        {TABS.map(t => {
          const active = filter === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setFilter(t.id)}
              style={{
                padding: '8px 14px', borderRadius: 999,
                border: `1.5px solid ${active ? colors.accent : colors.cardBorder}`,
                backgroundColor: active ? colors.accent : colors.card,
                color: active ? '#020617' : colors.textSub,
                fontSize: 12, fontWeight: 700,
                minHeight: 44, cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <div style={{ padding: 16 }}>
        {filtered.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 32px', textAlign: 'center' }}>
            <div style={{
              width: 110, height: 110, borderRadius: 55,
              backgroundColor: colors.accentSoft,
              border: `2px solid ${colors.accent}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 12,
            }}>
              <Icon name="envelope-open" size={44} color={colors.accentText} />
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: colors.text, marginTop: 4 }}>
              You're all caught up!
            </div>
            <div style={{ fontSize: 13, color: colors.textSub, lineHeight: 1.55, marginTop: 8 }}>
              No new notifications at the moment.<br />We'll notify you when something important happens.
            </div>
            {notifications.length > 0 && (
              <button
                onClick={handleMarkAllRead}
                style={{
                  marginTop: 18, padding: '12px 22px',
                  borderRadius: 999, border: `1.5px solid ${colors.accent}`,
                  backgroundColor: 'transparent',
                  color: colors.accentText,
                  fontSize: 13, fontWeight: 800,
                  minHeight: 50, cursor: 'pointer',
                }}
              >
                Mark all as read
              </button>
            )}
          </div>
        ) : (
          filtered.map(item => <NotificationItem key={item.id} item={item} />)
        )}
      </div>
    </div>
  );
}
